// ---------------------------------------------------------------------------
// step-3-details.tsx — Project details: name (required), description
// (optional), target platform radio group, and DS summary read-only.
// ---------------------------------------------------------------------------

import React from 'react'
import { Check } from 'lucide-react'
import styles from '../new-project-wizard-page.module.css'
import { useWizard } from '../wizard-state'
import {
  type StudioAppClass,
  type StudioRuntimeTarget,
  type StudioSecurityProfile,
} from '../../lib/studio-project-plan'
import { useStudioLocale } from '@/i18n'
import type { NewProjectCopy } from '@/i18n/studio-new-project-copy'

/**
 * The option ORDER, which is layout and not copy. The label and the
 * description beside each value come from the dictionary, keyed by the same
 * value — so a locale cannot drop an option and an option cannot lose its
 * words (`step3.appClass` etc. are `Record`-shaped and required).
 */
const APP_CLASS_ORDER: StudioAppClass[] = ['static', 'dynamic', 'hybrid']
const RUNTIME_TARGET_ORDER: StudioRuntimeTarget[] = [
  'web',
  'mobile',
  'flutter',
  'rn',
  'desktop',
]
const SECURITY_PROFILE_ORDER: StudioSecurityProfile[] = [
  'standard',
  'enterprise',
  'financial',
]

export function Step3Details() {
  const copy: NewProjectCopy['step3'] =
    useStudioLocale().strings.newProject.step3
  const { state, dispatch } = useWizard()

  const dsName =
    state.source === 'template'
      ? copy.fromStarterSuffix(state.selectedTemplateName ?? '')
      : (state.selectedPresetName ?? '—')

  return (
    <div className={styles.stepContent}>
      <div className={styles.stepHeading}>
        <h1 className={styles.stepH1}>{copy.heading}</h1>
        <p className={styles.stepLead}>{copy.lead}</p>
      </div>

      {/* Project name */}
      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="project-name">
          {copy.nameLabel}{' '}
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="project-name"
          type="text"
          className={styles.fieldInput}
          value={state.projectName}
          onChange={(e) =>
            dispatch({ type: 'SET_PROJECT_NAME', name: e.target.value })
          }
          placeholder={copy.namePlaceholder}
          required
          autoFocus
        />
        {state.projectName.trim().length === 0 && (
          <p className={styles.fieldHint}>{copy.nameRequiredHint}</p>
        )}
      </div>

      {/* Description */}
      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel} htmlFor="project-desc">
          {copy.descriptionLabel}{' '}
          <span className={styles.fieldOptional}>
            {copy.descriptionOptional}
          </span>
        </label>
        <textarea
          id="project-desc"
          className={styles.fieldTextarea}
          value={state.projectDescription}
          onChange={(e) =>
            dispatch({
              type: 'SET_PROJECT_DESCRIPTION',
              description: e.target.value,
            })
          }
          placeholder={copy.descriptionPlaceholder}
          rows={3}
        />
      </div>

      {/* App class */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>{copy.workType}</p>
        <div className={styles.platformGroup}>
          {APP_CLASS_ORDER.map((value) => (
            <label
              key={value}
              data-testid={`new-project-app-class-${value}`}
              className={[
                styles.platformOption,
                state.appClass === value ? styles.platformOptionSelected : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <input
                type="radio"
                name="app-class"
                value={value}
                checked={state.appClass === value}
                onChange={() =>
                  dispatch({ type: 'SET_APP_CLASS', appClass: value })
                }
                className={styles.srOnly}
              />
              {state.appClass === value && (
                <Check
                  size={14}
                  className="absolute right-3 top-3 text-primary"
                  aria-hidden
                />
              )}
              <span className={styles.platformLabel}>
                {copy.appClass[value].label}
              </span>
              <span className={styles.platformDesc}>
                {copy.appClass[value].desc}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Target platform */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>{copy.whereItRuns}</p>
        <div className={styles.platformGroup}>
          {RUNTIME_TARGET_ORDER.map((value) => (
            <label
              key={value}
              data-testid={`new-project-runtime-${value}`}
              className={[
                styles.platformOption,
                state.runtimeTargets.includes(value)
                  ? styles.platformOptionSelected
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <input
                type="checkbox"
                name="runtime-target"
                value={value}
                checked={state.runtimeTargets.includes(value)}
                onChange={() =>
                  dispatch({ type: 'TOGGLE_RUNTIME_TARGET', target: value })
                }
                className={styles.srOnly}
              />
              {state.runtimeTargets.includes(value) && (
                <Check
                  size={14}
                  className="absolute right-3 top-3 text-primary"
                  aria-hidden
                />
              )}
              <span className={styles.platformLabel}>
                {copy.runtime[value].label}
              </span>
              <span className={styles.platformDesc}>
                {copy.runtime[value].desc}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Security profile */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>{copy.reviewLevel}</p>
        <div className={styles.platformGroup}>
          {SECURITY_PROFILE_ORDER.map((value) => (
            <label
              key={value}
              data-testid={`new-project-security-${value}`}
              className={[
                styles.platformOption,
                state.securityProfile === value
                  ? styles.platformOptionSelected
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <input
                type="radio"
                name="security-profile"
                value={value}
                checked={state.securityProfile === value}
                onChange={() =>
                  dispatch({ type: 'SET_SECURITY_PROFILE', profile: value })
                }
                className={styles.srOnly}
              />
              {state.securityProfile === value && (
                <Check
                  size={14}
                  className="absolute right-3 top-3 text-primary"
                  aria-hidden
                />
              )}
              <span className={styles.platformLabel}>
                {copy.security[value].label}
              </span>
              <span className={styles.platformDesc}>
                {copy.security[value].desc}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Read-only summary */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{copy.summaryStartingPoint}</span>
          <span className={styles.summaryVal}>
            {state.starterPathName ??
              (state.source === 'template'
                ? copy.starterFallback
                : copy.fromScratch)}
          </span>
        </div>
        {state.source === 'template' && state.selectedTemplateName && (
          <div className={styles.summaryRow}>
            <span className={styles.summaryKey}>{copy.summaryStarter}</span>
            <span className={styles.summaryVal}>
              {state.selectedTemplateName}
            </span>
          </div>
        )}
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{copy.summaryStyle}</span>
          <span className={styles.summaryVal}>{dsName}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryKey}>{copy.summaryWorkShape}</span>
          <span className={styles.summaryVal}>
            {copy.appClass[state.appClass].label} ·{' '}
            {copy.security[state.securityProfile].label}
          </span>
        </div>
      </div>
    </div>
  )
}
